/**
 * ============================================================
 * BORN FRAGRANCE — FLOATING BOTTOM NAVIGATION v2.0
 * bf-floating-nav.js
 * ============================================================
 * Responsibilities:
 * 1. Entrance animation — fade + slide + scale on load.
 * 2. Scroll behavior — subtle opacity reduction on scroll-down.
 * 3. Search — open Horizon dialog-component search modal + focus.
 * 4. Cart badge — live count via cart.js + Horizon events.
 * 5. Wishlist badge — localStorage-based, event-driven.
 * 6. Active state — highlight current page item.
 * 7. Body / template classes — CSS padding + ATC suppression.
 *
 * Performance:
 * - Single fetch on load for cart count.
 * - Event-driven updates — no polling.
 * - Scroll handler uses requestAnimationFrame + passive listener.
 * - No duplicate listeners. Singleton pattern.
 * ============================================================ */

(function () {
  'use strict';

  const nav = document.getElementById('bf-floating-nav');
  if (!nav) return;

  /* ── 1. Body / template classes ── */
  document.body.classList.add('bf-has-floating-nav');

  const mainContent = document.getElementById('MainContent');
  if (mainContent && mainContent.dataset.template) {
    const tpl = mainContent.dataset.template.split('.')[0];
    if (tpl) document.body.classList.add('template-' + tpl);
  }

  /* ── 2. Entrance animation ── */
  function showNav() {
    requestAnimationFrame(function () {
      nav.classList.add('bf-nav--visible');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(showNav, 220);
    }, { once: true });
  } else {
    setTimeout(showNav, 220);
  }

  /* ── 3. Scroll behavior ── */
  var lastScrollY = window.scrollY;
  var scrollRafId = null;
  var SCROLL_THRESHOLD = 60; // px before dimming kicks in

  function onScroll() {
    if (scrollRafId) return;
    scrollRafId = requestAnimationFrame(function () {
      scrollRafId = null;
      var currentY = window.scrollY;
      var delta = currentY - lastScrollY;

      if (delta > 8 && currentY > SCROLL_THRESHOLD) {
        // Scrolling down — dim slightly
        nav.classList.add('bf-nav--scrolled-down');
      } else if (delta < -4 || currentY <= SCROLL_THRESHOLD) {
        // Scrolling up or near top — restore
        nav.classList.remove('bf-nav--scrolled-down');
      }
      lastScrollY = currentY;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });

  /* ── 4. Search ── */
  // Horizon uses <dialog-component id="search-modal"> with an inner <dialog ref="dialog">
  // Opening strategy: find the dialog element and call .showModal(), then focus input.
  const searchBtn = nav.querySelector('[data-bf-search]');

  if (searchBtn) {
    searchBtn.addEventListener('click', function (e) {
      e.preventDefault();

      var opened = false;

      // Strategy A: Horizon dialog-component — find inner <dialog> and showModal()
      var dialogComponent = document.getElementById('search-modal');
      if (dialogComponent) {
        var innerDialog = dialogComponent.querySelector('dialog[ref="dialog"], dialog');
        if (innerDialog && typeof innerDialog.showModal === 'function' && !innerDialog.open) {
          try {
            innerDialog.showModal();
            opened = true;
          } catch (err) { /* cross-origin guard */ }
        } else if (innerDialog && innerDialog.open) {
          opened = true; // already open
        }
      }

      // Strategy B: Click the header search opener button (Horizon renders search-modal-opener)
      if (!opened) {
        var openerBtn = document.querySelector(
          'search-modal-opener button, [data-search-toggle], [aria-controls="search-modal"], .header__search-toggle'
        );
        if (openerBtn) {
          openerBtn.click();
          opened = true;
        }
      }

      // Strategy C: dispatch custom event Horizon listens to
      if (!opened) {
        document.dispatchEvent(new CustomEvent('search-modal:open', { bubbles: true }));
        var modalEl = document.querySelector('search-modal, [data-search-modal]');
        if (modalEl) {
          modalEl.dispatchEvent(new CustomEvent('modal:open', { bubbles: true }));
          opened = true;
        }
      }

      // Strategy D: Hard fallback — navigate to /search
      if (!opened) {
        window.location.href = '/search';
        return;
      }

      // Focus the search input after a short paint delay
      setTimeout(function () {
        var searchInput = document.querySelector(
          '#search-modal input[type="search"], #search-modal .search-input, input#cmdk-input'
        );
        if (searchInput) searchInput.focus();
      }, 120);
    });
  }

  /* ── 5. Cart badge ── */
  var cartBadge = nav.querySelector('[data-bf-cart-badge]');

  function updateCartBadge(count) {
    if (!cartBadge) return;
    var c = parseInt(count, 10) || 0;
    cartBadge.dataset.count = c > 0 ? String(c) : '0';
    cartBadge.textContent = c > 9 ? '9+' : (c > 0 ? String(c) : '');
  }

  function fetchCartCount() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) { updateCartBadge(data.item_count); })
      .catch(function () { /* silent — badge stays at last known value */ });
  }

  // Initial fetch — deferred to idle
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(fetchCartCount, { timeout: 2000 });
  } else {
    setTimeout(fetchCartCount, 400);
  }

  // Live updates via Horizon theme events
  function handleCartEvent(e) {
    var count = (e.detail && e.detail.cart && e.detail.cart.item_count != null)
      ? e.detail.cart.item_count
      : (e.detail && e.detail.item_count != null)
        ? e.detail.item_count
        : null;

    if (count !== null) {
      updateCartBadge(count);
    } else {
      fetchCartCount();
    }
  }

  document.addEventListener('cart:updated', handleCartEvent);
  document.addEventListener('cart-lines:update', handleCartEvent);
  document.addEventListener('shopify:cart:updated', handleCartEvent);
  // Horizon StandardEvents
  document.addEventListener('on:cart:add', handleCartEvent);
  document.addEventListener('on:cart:update', handleCartEvent);

  /* ── 6. Wishlist badge ── */
  var wishlistBadge = nav.querySelector('[data-bf-wishlist-badge]');

  function updateWishlistBadge() {
    if (!wishlistBadge) return;
    try {
      var stored =
        localStorage.getItem('bf-wishlist-count') ||
        localStorage.getItem('swym-wishlist-count') ||
        localStorage.getItem('wishlist-count') ||
        '0';
      // Also check BF native wishlist storage
      var bfWishlist = localStorage.getItem('bf-wishlist');
      if (bfWishlist) {
        try {
          var items = JSON.parse(bfWishlist);
          if (Array.isArray(items)) stored = String(items.length);
        } catch (e) {}
      }
      var c = parseInt(stored, 10) || 0;
      wishlistBadge.dataset.count = c > 0 ? String(c) : '0';
      wishlistBadge.textContent = c > 9 ? '9+' : (c > 0 ? String(c) : '');
    } catch (e) { /* localStorage blocked */ }
  }

  updateWishlistBadge();

  document.addEventListener('bf:wishlist:updated', updateWishlistBadge);
  document.addEventListener('swym:wishlist-updated', updateWishlistBadge);
  document.addEventListener('wishlist:updated', updateWishlistBadge);

  /* ── 7. Active state ── */
  var currentPath = window.location.pathname;

  nav.querySelectorAll('.bf-nav__item[data-bf-path]').forEach(function (item) {
    var path = item.dataset.bfPath;
    if (!path) return;
    var isActive = path === '/'
      ? currentPath === '/'
      : currentPath === path || currentPath.startsWith(path + '/');
    if (isActive) {
      item.classList.add('bf-nav--active');
      item.setAttribute('aria-current', 'page');
    }
  });

  // Mark search active when search modal is open
  var searchItem = nav.querySelector('[data-bf-search]');
  if (searchItem) {
    var observer = new MutationObserver(function () {
      var dialog = document.querySelector('#search-modal dialog');
      var isOpen = dialog && dialog.open;
      searchItem.classList.toggle('bf-nav--active', !!isOpen);
    });
    var dialogToObserve = document.querySelector('#search-modal');
    if (dialogToObserve) {
      observer.observe(dialogToObserve, { attributes: true, subtree: true, attributeFilter: ['open'] });
    }
  }

})();
